import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, Camera, PenLine, FileText } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";
import { PhotoStrip, PhotoUpload } from "@/components/Photos";

export const PROPOSAL_LABEL = { pending: "Čeká na odsouhlasení", approved: "Schváleno", rejected: "Zamítnuto" };
export const PROPOSAL_CLS = { pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-red-50 text-red-600 border-red-200" };

export function ExtraItemsList({ rep }) {
  const items = rep.extra_items || [];
  if (!items.length) return <p className="text-sm text-slate-800" data-testid="approval-proposal">{rep.proposal}</p>;
  return (
    <div className="text-sm text-slate-800 space-y-1" data-testid="approval-proposal">
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="flex justify-between gap-2"><span>{it.name} – {it.qty} ks</span><span className="text-slate-600">{it.price ? `cca ${it.price * it.qty} Kč` : "cena dle dohody"}</span></li>
        ))}
      </ul>
      {rep.proposal_text && <p className="text-xs text-slate-600">{rep.proposal_text}</p>}
      {rep.extra_total > 0 && <div className="font-semibold border-t border-amber-200 pt-1">Orientační cena celkem: {rep.extra_total} Kč</div>}
    </div>
  );
}

export const hasCounts = (rep) => COUNT_FIELDS.every(([k]) => typeof rep?.[k] === "number");

export function CountsEditor({ r, onSaved }) {
  const rep = r.report || {};
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => Object.fromEntries(COUNT_FIELDS.map(([k]) => [k, rep[k] ?? ""])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const missing = COUNT_FIELDS.filter(([k]) => f[k] === "");
    if (missing.length) { toast.error(`Vyplňte: ${missing.map((m) => m[1]).join(", ")}`); return; }
    setBusy(true);
    try { await api.patch(`/reservations/${r.id}/report/counts`, Object.fromEntries(COUNT_FIELDS.map(([k]) => [k, Number(f[k])]))); toast.success("Počty uloženy"); setOpen(false); onSaved?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className={`text-xs underline ${hasCounts(rep) ? "text-slate-500" : "text-amber-700 font-medium"}`} data-testid={`counts-edit-${r.id}`}>
      {hasCounts(rep) ? "Upravit počty" : "Doplnit počty (křídla, dveře, těsnění)"}
    </button>
  );
  return (
    <div className="border rounded-lg p-2 bg-white space-y-2" data-testid={`counts-form-${r.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {COUNT_FIELDS.map(([k, label, tid]) => (
          <div key={k} className="space-y-1">
            <Label className="text-[11px] leading-tight">{label}</Label>
            <Input type="number" inputMode="numeric" min="0" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })} className="h-9" data-testid={`counts-${tid}-${r.id}`} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy} style={{ background: "#0A2540" }} data-testid={`counts-save-${r.id}`}>Uložit počty</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Zrušit</Button>
      </div>
    </div>
  );
}

export function ReportSummary({ report, testId }) {
  if (!report) return null;
  return (
    <div className="text-xs text-slate-700 space-y-0.5" data-testid={testId}>
      <div><span className="font-medium text-[#0A2540]">Zápis:</span> kompletní servis – {hasCounts(report) ? countsText(report) : <span className="text-amber-700">počty nedoplněny</span>}{report.other_work ? ` · ${report.other_work}` : ""}</div>
      {report.proposal && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="font-medium text-[#B45309]">Vícepráce (díly):</span> {report.proposal}
          <span className={`px-1.5 py-0.5 rounded border text-[10px] ${PROPOSAL_CLS[report.proposal_status] || ""}`} data-testid={`${testId}-proposal-status`}>{PROPOSAL_LABEL[report.proposal_status] || ""}</span>
        </div>
      )}
    </div>
  );
}

export const countsText = (rep) => `okenních křídel ${rep.windows_count ?? 0} · balkonových křídel ${rep.balcony_doors ?? 0} · vert. těsnění lodžie ${rep.vertical_seals ?? 0} ks · horiz. těsnění ${rep.horizontal_seal_cm ?? 0} cm`;

const COUNT_FIELDS = [
  ["windows_count", "Počet okenních křídel", "windows"],
  ["balcony_doors", "Počet balkonových křídel", "balcony"],
  ["vertical_seals", "Vertikální těsnění lodžie (ks, 1 ks = 1,6 m)", "vseals"],
  ["horizontal_seal_cm", "Horizontální těsnění (cm)", "hseal"],
];

const EMPTY = { windows_count: "", balcony_doors: "", vertical_seals: "", horizontal_seal_cm: "", other_work: "", extra_items: [], proposal: "", resident_name: "", resident_signature: "" };

function ExtraItemsEditor({ items, onChange, rid }) {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const total = items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);
  return (
    <div className="space-y-2 border border-amber-200 bg-amber-50 rounded-lg p-2" data-testid={`report-extras-${rid}`}>
      <Label className="text-xs text-[#B45309]">Mechanická závada – návrh výměny dílů (vícepráce, schvaluje předseda)</Label>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_60px_84px_32px] gap-1 items-center">
          <Input value={it.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Díl – např. klika, pant, kování" className="h-9 bg-white" data-testid={`report-extra-name-${rid}-${i}`} />
          <Input type="number" inputMode="numeric" min="1" value={it.qty} onChange={(e) => upd(i, "qty", Math.max(1, parseInt(e.target.value || "1", 10) || 1))} className="h-9 bg-white" title="ks" data-testid={`report-extra-qty-${rid}-${i}`} />
          <Input type="number" inputMode="numeric" min="0" value={it.price} onChange={(e) => upd(i, "price", Math.max(0, parseInt(e.target.value || "0", 10) || 0))} placeholder="Kč/ks" className="h-9 bg-white" data-testid={`report-extra-price-${rid}-${i}`} />
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-600 flex justify-center" data-testid={`report-extra-del-${rid}-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, { name: "", qty: 1, price: 0 }])} className="h-8 bg-white" data-testid={`report-extra-add-${rid}`}><Plus className="h-4 w-4 mr-1" /> Přidat díl</Button>
        {total > 0 && <span className="text-xs font-semibold text-amber-800">Celkem cca {total} Kč</span>}
      </div>
      {items.length > 0 && <p className="text-[11px] text-amber-800 flex items-center gap-1"><Camera className="h-3 w-3" /> Přiložte fotky poškození (tlačítko „Přidat fotku“) – jsou součástí zápisu pro předsedu.</p>}
    </div>
  );
}

export function ServiceReportForm({ r, onSaved }) {
  const init = r.report ? { ...EMPTY, ...r.report, resident_signature: "", proposal: r.report.proposal_text ?? (r.report.extra_items?.length ? "" : r.report.proposal || "") } : { ...EMPTY, resident_name: r.name || r.owner_name || "" };
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/reservations/${r.id}/report.pdf`;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const missing = COUNT_FIELDS.filter(([k]) => f[k] === "" || f[k] === null || f[k] === undefined);
      if (missing.length) { toast.error(`Vyplňte: ${missing.map((m) => m[1]).join(", ")}`); setBusy(false); return; }
      const body = { windows_count: Number(f.windows_count), balcony_doors: Number(f.balcony_doors), vertical_seals: Number(f.vertical_seals), horizontal_seal_cm: Number(f.horizontal_seal_cm), other_work: f.other_work, proposal: f.proposal, extra_items: f.extra_items.filter((it) => it.name.trim()), resident_name: f.resident_name, resident_signature: f.resident_signature };
      await api.post(`/tech/reservations/${r.id}/report`, body); toast.success("Zápis ze servisu uložen (stav Hotovo)"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!open) return (
    <div className="space-y-1">
      <ReportSummary report={r.report} testId={`tech-report-summary-${r.id}`} />
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" variant={r.report ? "ghost" : "outline"} onClick={() => setOpen(true)} data-testid={`tech-report-open-${r.id}`}><ClipboardList className="h-4 w-4 mr-1" /> {r.report ? "Upravit protokol" : "Protokol o servisu"}</Button>
        {r.report && <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#0A2540] underline" data-testid={`tech-report-pdf-${r.id}`}><FileText className="h-4 w-4" /> Protokol PDF</a>}
        {r.report && <span className={`text-xs px-2 py-0.5 rounded ${r.report.resident_signed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`tech-report-signed-${r.id}`}>{r.report.resident_signed ? "Podepsáno obyvatelem" : "Bez podpisu obyvatele"}</span>}
      </div>
    </div>
  );
  return (
    <div className="border rounded-lg p-3 space-y-3 bg-slate-50" data-testid={`tech-report-form-${r.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`report-counts-${r.id}`}>
        {COUNT_FIELDS.map(([k, label, tid]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{label} <span className="text-red-600">*</span></Label>
            <Input type="number" inputMode="numeric" min="0" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })} className="h-10 bg-white" data-testid={`report-${tid}-${r.id}`} />
          </div>
        ))}
      </div>
      <div className="space-y-1"><Label className="text-xs">Poznámka k servisu</Label><Textarea rows={2} value={f.other_work} onChange={(e) => setF({ ...f, other_work: e.target.value })} placeholder="např. obyvatel upozorněn na větrání, vše v pořádku" data-testid={`report-other-${r.id}`} /></div>
      <ExtraItemsEditor items={f.extra_items} onChange={(extra_items) => setF({ ...f, extra_items })} rid={r.id} />
      {f.extra_items.length > 0 && <div className="space-y-1"><Label className="text-xs">Popis závady / doplnění k víceprácím</Label><Textarea rows={2} value={f.proposal} onChange={(e) => setF({ ...f, proposal: e.target.value })} placeholder="např. prasklý pant balkonových dveří, nelze seřídit" data-testid={`report-proposal-${r.id}`} /></div>}
      <div className="space-y-2 border rounded-lg p-2 bg-white" data-testid={`report-photos-${r.id}`}>
        <Label className="text-xs flex items-center gap-1"><Camera className="h-3 w-3" /> Fotodokumentace ({(r.photos || []).length}) – vyfoťte nebo vyberte více fotek z galerie; uvidí je předseda a budou v protokolu</Label>
        <PhotoStrip photos={r.photos} canDelete onDeleted={onSaved} testPrefix={`report-photo-${r.id}`} />
        <PhotoUpload reservationId={r.id} onUploaded={onSaved} compact />
      </div>
      <div className="space-y-2 border rounded-lg p-2 bg-white">
        <Label className="text-xs flex items-center gap-1"><PenLine className="h-3 w-3" /> Podpis obyvatele jednotky (prstem) – potvrzuje provedení servisu a seznámení s doporučením. Po podpisu obyvatel dostane kopii protokolu e-mailem{r.email ? ` (${r.email})` : ""}.</Label>
        <Input value={f.resident_name} onChange={(e) => setF({ ...f, resident_name: e.target.value })} placeholder="Jméno podepisujícího" className="h-9" data-testid={`report-resident-name-${r.id}`} />
        {r.report?.resident_signed && !f.resident_signature && <p className="text-[11px] text-emerald-700">Podpis už je uložen – novým podpisem ho nahradíte.</p>}
        <SignaturePad onChange={(v) => setF({ ...f, resident_signature: v })} testId={`report-resident-signature-${r.id}`} />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy} style={{ background: "#0A2540" }} data-testid={`report-save-${r.id}`}>Uložit protokol</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Zrušit</Button>
      </div>
    </div>
  );
}

export function ProposalDecision({ r, onDone }) {
  const rep = r.report;
  if (!rep?.proposal) return null;
  const decide = async (decision) => {
    const comment = decision === "rejected" ? (window.prompt("Důvod zamítnutí (nepovinné):") ?? "") : "";
    try { await api.post(`/reservations/${r.id}/proposal`, { decision, comment }); toast.success(decision === "approved" ? "Návrh schválen" : "Návrh zamítnut"); onDone?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-1" data-testid={`proposal-${r.id}`}>
      <ReportSummary report={rep} testId={`res-report-${r.id}`} />
      {rep.proposal_status === "pending" && (
        <div className="flex gap-1">
          <Button size="sm" className="h-7" onClick={() => decide("approved")} style={{ background: "#065F46" }} data-testid={`proposal-approve-${r.id}`}>Schválit</Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => decide("rejected")} data-testid={`proposal-reject-${r.id}`}>Zamítnout</Button>
        </div>
      )}
      {rep.proposal_status !== "pending" && rep.proposal_decided_by && <div className="text-[10px] text-slate-400">{PROPOSAL_LABEL[rep.proposal_status]} · {rep.proposal_decided_by}{rep.proposal_comment ? ` · ${rep.proposal_comment}` : ""}</div>}
      <div className="flex gap-2">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/reservations/${r.id}/report.pdf`} target="_blank" rel="noreferrer" className="text-[10px] text-[#0A2540] underline" data-testid={`report-pdf-${r.id}`}>Zápis PDF</a>
        <button onClick={async () => { try { await api.post(`/reservations/${r.id}/report/resend`); toast.success("Zápis znovu odeslán předsedovi"); } catch (e) { toast.error(apiErr(e)); } }} className="text-[10px] text-slate-500 underline" data-testid={`report-resend-${r.id}`}>Poslat předsedovi znovu</button>
      </div>
    </div>
  );
}
